import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAlumnos, getExpedientes, createExpediente, updateExpediente, deleteExpediente, getCiclos } from '../lib/api';
import { formatNombreCompleto } from '../lib/nombre';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus, MagnifyingGlass, Eye, PencilSimple, Funnel, FileText, Warning, Trash
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';

const TIPOS_INCIDENCIA = [
  { value: 'salida_temprana', label: 'Salida Temprana Autorizada' },
  { value: 'conducta', label: 'Conducta' },
  { value: 'reporte_academico', label: 'Reporte Académico Docente' },
  { value: 'incumplimiento_tareas', label: 'Incumplimiento de Tareas' },
  { value: 'conflicto_alumnos', label: 'Conflicto entre Alumnos' },
  { value: 'citatorio_padres', label: 'Citatorio a Padres' },
  { value: 'seguimiento_orientacion', label: 'Seguimiento de Orientación' },
  { value: 'observacion_general', label: 'Observación General' }
];

const ExpedientePage = () => {
  const { user, hasPermission } = useAuth();
  const [expedientes, setExpedientes] = useState([]);
  const [alumnos, setAlumnos] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedExpediente, setSelectedExpediente] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchAlumno, setSearchAlumno] = useState('');

  const [filters, setFilters] = useState({
    matricula: '',
    tipo: '',
    gravedad: '',
    grado: '',
    grupo: '',
    turno: user?.turno || '',
    incluir_egresados: false,
  });

  const [formData, setFormData] = useState({
    alumno_id: '',
    matricula: '',
    tipo: '',
    descripcion: '',
    gravedad: '',
    sancion: ''
  });

  const fetchExpedientes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value === true) params[key] = true;
        else if (value && value !== 'all') params[key] = value;
      });
      
      const response = await getExpedientes(params);
      setExpedientes(response.data);
    } catch (error) {
      toast.error('Error al cargar expedientes');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchExpedientes();
  }, [fetchExpedientes]);

  useEffect(() => {
    getCiclos().then(r => setCiclos(r.data || [])).catch(() => setCiclos([]));
  }, []);

  const cicloNombre = useCallback((cicloId) => {
    const c = ciclos.find(x => x.id === cicloId);
    return c?.nombre || '';
  }, [ciclos]);

  const searchAlumnos = async (query) => {
    if (query.length < 2) {
      setAlumnos([]);
      return;
    }
    try {
      const response = await getAlumnos({ nombre: query, limit: 10 });
      setAlumnos(response.data.alumnos);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error searching alumnos:', error);
      }
    }
  };

  const handleAlumnoSelect = (alumno) => {
    setFormData(prev => ({
      ...prev,
      alumno_id: alumno.id,
      matricula: alumno.matricula
    }));
    setSearchAlumno(`${formatNombreCompleto(alumno)} - ${alumno.matricula}`);
    setAlumnos([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && selectedExpediente) {
        await updateExpediente(selectedExpediente.id, formData.descripcion, formData.sancion);
        toast.success('Expediente actualizado');
      } else {
        await createExpediente(formData);
        toast.success('Expediente registrado');
      }
      setShowModal(false);
      resetForm();
      fetchExpedientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEliminar = (expediente) => {
    setDeleteTarget(expediente);
  };

  const confirmEliminar = async (motivo) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteExpediente(deleteTarget.id, motivo);
      toast.success('Registro eliminado correctamente');
      setDeleteTarget(null);
      fetchExpedientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (expediente) => {
    setSelectedExpediente(expediente);
    setFormData({
      descripcion: expediente.descripcion,
      sancion: expediente.sancion || ''
    });
    setEditMode(true);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      alumno_id: '',
      matricula: '',
      tipo: '',
      descripcion: '',
      gravedad: '',
      sancion: ''
    });
    setSearchAlumno('');
    setSelectedExpediente(null);
    setEditMode(false);
  };

  const getGravedadColor = (gravedad) => {
    const colors = {
      leve: 'bg-blue-100 text-blue-800',
      media: 'bg-amber-100 text-amber-800',
      grave: 'bg-red-100 text-red-800'
    };
    return colors[gravedad] || 'bg-gray-100 text-gray-800';
  };

  const getTipoLabel = (tipo) => {
    return TIPOS_INCIDENCIA.find(t => t.value === tipo)?.label || tipo;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="expediente-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Expediente</h1>
          <p className="text-muted-foreground">Registro de incidencias de alumnos</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Funnel size={16} className="mr-1" />
            Filtros
          </Button>
          
          {hasPermission(['administrador', 'direccion', 'prefectura']) && (
            <Button 
              size="sm"
              onClick={() => { resetForm(); setShowModal(true); }}
              data-testid="add-expediente"
            >
              <Plus size={16} className="mr-1" />
              Nueva Incidencia
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <Label className="text-xs">Matrícula</Label>
                <Input
                  placeholder="Buscar..."
                  value={filters.matricula}
                  onChange={(e) => setFilters(p => ({ ...p, matricula: e.target.value }))}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={filters.tipo} onValueChange={(v) => setFilters(p => ({ ...p, tipo: v }))}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {TIPOS_INCIDENCIA.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Gravedad</Label>
                <Select value={filters.gravedad} onValueChange={(v) => setFilters(p => ({ ...p, gravedad: v }))}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="leve">Leve</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="grave">Grave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Grado</Label>
                <Select value={filters.grado} onValueChange={(v) => setFilters(p => ({ ...p, grado: v }))}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="1">1°</SelectItem>
                    <SelectItem value="2">2°</SelectItem>
                    <SelectItem value="3">3°</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Grupo</Label>
                <Select value={filters.grupo} onValueChange={(v) => setFilters(p => ({ ...p, grupo: v }))}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="A">A</SelectItem>
                    <SelectItem value="B">B</SelectItem>
                    <SelectItem value="C">C</SelectItem>
                    <SelectItem value="D">D</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setFilters({ matricula: '', tipo: '', gravedad: '', grado: '', grupo: '', turno: user?.turno || '', incluir_egresados: false })}
                  className="w-full"
                >
                  Limpiar
                </Button>
              </div>
            </div>

            {hasPermission(['administrador', 'direccion']) && (
              <div className="flex items-center gap-2 pt-3 mt-3 border-t">
                <input
                  id="incluir-egresados-exp"
                  type="checkbox"
                  className="h-4 w-4 rounded border-input"
                  checked={filters.incluir_egresados}
                  onChange={(e) => setFilters(p => ({ ...p, incluir_egresados: e.target.checked }))}
                  data-testid="toggle-egresados-expediente"
                />
                <Label htmlFor="incluir-egresados-exp" className="cursor-pointer text-sm">
                  Mostrar incidencias de alumnos <strong>egresados</strong> (archivados)
                </Label>
                {filters.incluir_egresados && (
                  <Badge variant="secondary" className="ml-2 text-xs">Vista de archivo activa</Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <FileText size={20} />
            {expedientes.length} registros encontrados
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[220px]">Alumno</TableHead>
                  <TableHead className="w-[150px]">Tipo</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="w-[100px]">Gravedad</TableHead>
                  <TableHead className="w-[130px]">Registrada en</TableHead>
                  <TableHead className="w-[120px]">Fecha</TableHead>
                  <TableHead className="w-[100px] text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="spinner mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : expedientes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      <Warning size={40} className="mx-auto mb-2 opacity-50" />
                      No se encontraron incidencias
                    </TableCell>
                  </TableRow>
                ) : (
                  expedientes.map((exp) => (
                    <TableRow key={exp.id} className="hover:bg-muted/30">
                      <TableCell className="text-sm">
                        <div className="flex flex-col leading-tight">
                          <span className="font-mono text-xs">{exp.matricula}</span>
                          {exp.nombre_alumno && (
                            <span className="text-xs break-words whitespace-normal max-w-[200px]" data-testid={`exp-nombre-${exp.matricula}`}>
                              {exp.nombre_alumno}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{getTipoLabel(exp.tipo)}</TableCell>
                      <TableCell className="max-w-[300px] truncate">{exp.descripcion}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${getGravedadColor(exp.gravedad)}`}>
                          {exp.gravedad}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="flex flex-col leading-tight">
                          <span className="font-medium">{exp.grado}° {exp.grupo}</span>
                          <span className="text-muted-foreground truncate max-w-[120px]" title={cicloNombre(exp.ciclo_escolar_id)}>
                            Ciclo {cicloNombre(exp.ciclo_escolar_id) || '—'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {exp.fecha_registro && format(new Date(exp.fecha_registro), 'dd/MM/yyyy', { locale: es })}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => { setSelectedExpediente(exp); setShowDetailModal(true); }}
                          >
                            <Eye size={16} />
                          </Button>
                          {hasPermission(['administrador', 'direccion']) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEditModal(exp)}
                            >
                              <PencilSimple size={16} />
                            </Button>
                          )}
                          {hasPermission(['administrador', 'direccion']) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:bg-red-50"
                              onClick={() => handleEliminar(exp)}
                              title="Eliminar registro"
                              data-testid={`delete-expediente-${exp.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editMode ? 'Editar Incidencia' : 'Nueva Incidencia'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editMode && (
              <>
                <div>
                  <Label>Buscar Alumno *</Label>
                  <div className="relative">
                    <Input
                      placeholder="Escribe el nombre o matrícula..."
                      value={searchAlumno}
                      onChange={(e) => {
                        setSearchAlumno(e.target.value);
                        searchAlumnos(e.target.value);
                      }}
                      data-testid="search-alumno"
                    />
                    {alumnos.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-card border rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {alumnos.map(a => (
                          <button
                            key={a.id}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-muted text-xs sm:text-sm break-words whitespace-normal leading-tight"
                            onClick={() => handleAlumnoSelect(a)}
                          >
                            {formatNombreCompleto(a)} - {a.matricula} ({a.grado}° {a.grupo})
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Tipo de Incidencia *</Label>
                  <Select 
                    value={formData.tipo} 
                    onValueChange={(v) => setFormData(p => ({ ...p, tipo: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_INCIDENCIA.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Gravedad *</Label>
                  <Select 
                    value={formData.gravedad} 
                    onValueChange={(v) => setFormData(p => ({ ...p, gravedad: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="leve">Leve</SelectItem>
                      <SelectItem value="media">Media</SelectItem>
                      <SelectItem value="grave">Grave</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div>
              <Label>Descripción *</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData(p => ({ ...p, descripcion: e.target.value }))}
                placeholder="Describe la incidencia..."
                rows={4}
                required
                data-testid="descripcion-input"
              />
            </div>
            <div>
              <Label>Sanción (opcional)</Label>
              <Textarea
                value={formData.sancion}
                onChange={(e) => setFormData(p => ({ ...p, sancion: e.target.value }))}
                placeholder="Sanción aplicada..."
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={!editMode && (!formData.alumno_id || !formData.tipo || !formData.gravedad || !formData.descripcion)}
                data-testid="submit-expediente"
              >
                {editMode ? 'Guardar Cambios' : 'Registrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Detalle de Incidencia</DialogTitle>
          </DialogHeader>
          {selectedExpediente && (
            <div className="space-y-4">
              <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">
                <p className="font-medium">
                  Registrada en <strong>{selectedExpediente.grado}° {selectedExpediente.grupo}</strong> — Ciclo <strong>{cicloNombre(selectedExpediente.ciclo_escolar_id) || '—'}</strong>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  El grado y ciclo mostrados corresponden al momento en que se levantó la incidencia y se preservan aunque el alumno haya sido promovido.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Alumno</p>
                  <p className="font-mono text-sm">{selectedExpediente.matricula}</p>
                  {selectedExpediente.nombre_alumno && (
                    <p className="text-sm break-words">{selectedExpediente.nombre_alumno}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Grado y Grupo</p>
                  <p>{selectedExpediente.grado}° {selectedExpediente.grupo}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Turno</p>
                  <p className="capitalize">{selectedExpediente.turno}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fecha</p>
                  <p>{selectedExpediente.fecha_registro && format(new Date(selectedExpediente.fecha_registro), "d 'de' MMMM, yyyy", { locale: es })}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tipo</p>
                <p className="font-medium">{getTipoLabel(selectedExpediente.tipo)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gravedad</p>
                <Badge className={getGravedadColor(selectedExpediente.gravedad)}>
                  {selectedExpediente.gravedad}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Descripción</p>
                <p className="text-sm">{selectedExpediente.descripcion}</p>
              </div>
              {selectedExpediente.sancion && (
                <div>
                  <p className="text-sm text-muted-foreground">Sanción</p>
                  <p className="text-sm">{selectedExpediente.sancion}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
        title="Eliminar incidencia del expediente"
        description="El registro se marcará como eliminado y dejará de aparecer en el expediente, listado y reportes. Esta acción es irreversible."
        itemLabel={deleteTarget ? `${deleteTarget.tipo?.replace(/_/g,' ')} — Mat. ${deleteTarget.matricula} — ${(deleteTarget.descripcion || '').slice(0, 60)}` : ''}
        onConfirm={confirmEliminar}
        loading={deleting}
      />
    </div>
  );
};

export default ExpedientePage;
